import { proxy, ref, subscribe } from "@web-monorepo/hooks";
import { useEffect, useMemo, useRef } from "react";
import { createContext, useContext, ReactNode } from "react";
import { splitByCapacity } from "./Dropzone/utils";

class FileUploadStore {
  attached: boolean = false;
  error?: Error;
  dragReferences = 0;
  files: File[] = [];

  get isDragging() {
    return this.dragReferences > 0;
  }

  // Methods
  handleDrag(event: "enter" | "leave" | "drop") {
    switch (event) {
      case "enter":
        this.dragReferences += 1;
        break;
      case "leave":
        this.dragReferences -= 1;
        break;
      case "drop":
        this.dragReferences = 0;
        break;
    }
  }

  // important to store references to the files, not proxies:
  setFiles(newFiles: File[]) {
    this.files = newFiles.map(ref);
  }

  addFiles(newFiles: File[] | null | undefined, maxAllowed: number): { duplicates: File[]; tooMany: File[] } {
    if (!newFiles) return { duplicates: [], tooMany: [] };

    const { kept, duplicates, tooMany } = splitByCapacity(this.files, newFiles, maxAllowed);
    this.setFiles(kept);
    return { duplicates, tooMany };
  }

  deleteFile(file: File | null | undefined) {
    if (file) {
      this.files = this.files.filter((f) => f.name !== file.name);
    }
  }
}

export const createFileUploadStore = () => {
  return proxy(new FileUploadStore());
};

const FileUploadContext = createContext<ReturnType<typeof createFileUploadStore> | null>(null);

export const Provider = ({ children }: { children: ReactNode }) => {
  const store = useMemo(() => createFileUploadStore(), []);

  useEffect(() => {
    store.attached = true;
    return () => {
      store.attached = false;
    };
  }, [store]);

  if (store.error) throw store.error;

  return <FileUploadContext.Provider value={store}>{children}</FileUploadContext.Provider>;
};

let sketchyGlobalStore: undefined | ReturnType<typeof createFileUploadStore> = undefined;

export const useStore = () => {
  const store = useContext(FileUploadContext);
  if (!store) {
    return (sketchyGlobalStore ||= createFileUploadStore());
  }
  return store;
};

export const useOnFilesChange = (callback: (files: File[]) => void) => {
  const store = useStore();
  const lastValue = useRef(store.files);

  useEffect(
    () =>
      subscribe(
        store,
        () => {
          if (lastValue.current !== store.files) callback(store.files);
        },
        true,
      ),
    [store, callback],
  );
};
